/* SpawnAnything — replay file format (.awreplay).
   A replay is the battle's setup (exactly the payload the sim worker got) plus every command with the tick it was
   applied at; re-running the deterministic sim with both reproduces the battle bit for bit.

   In memory (decoded):
     { v, app, created, title, ticks, seed,
       sim:   setup payload for the worker (armies stats, positions, buildings, reserve, minW, terrainSpec …; no terrain arrays,
              those are regenerated from terrainSpec),
       scene: what main.js needs to draw it (army cfgs, generated unit types + recipes, placed buildings + AI building
              recipes, style, placement mode),
       cmds:  [[tick, cmd], …] in application order (cmd as the worker applied it: possess / input / bomb / throw / upg / speed),
       hashes:[[tick, hash], …] state hashes of the recording, checked during playback }
   On disk: JSON (typed arrays as base64, commands as short delta-coded tuples), gzip-compressed when the
   browser has CompressionStream. Pure module (browser + Node). */

export const REPLAY_VERSION = 1;
export const REPLAY_EXT = '.awreplay';
export const REPLAY_MIME = 'application/x-spawnanything-replay';
const APP_IDS = ['spawnanything', 'anything-wars']; // files from before the rename still play

/* ---- typed arrays <-> base64 ---- */
const TA = { f32: Float32Array, f64: Float64Array, i32: Int32Array, u32: Uint32Array, i16: Int16Array, u16: Uint16Array, i8: Int8Array, u8: Uint8Array };
const taName = a => Object.keys(TA).find(k => a instanceof TA[k]);
function b64(u8) {
  if (typeof Buffer !== 'undefined') return Buffer.from(u8.buffer, u8.byteOffset, u8.byteLength).toString('base64');
  let s = ''; for (let i = 0; i < u8.length; i += 0x8000) s += String.fromCharCode.apply(null, u8.subarray(i, i + 0x8000));
  return btoa(s);
}
function unb64(s) {
  if (typeof Buffer !== 'undefined') { const b = Buffer.from(s, 'base64'); return new Uint8Array(b.buffer, b.byteOffset, b.byteLength).slice(); }
  const bin = atob(s), u8 = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
  return u8;
}
const replacer = (k, v) => {
  if (ArrayBuffer.isView(v) && !(v instanceof DataView)) { const t = taName(v); if (t) return { $ta: t, d: b64(new Uint8Array(v.buffer, v.byteOffset, v.byteLength)) }; }
  return v;
};
const reviver = (k, v) => {
  if (v && typeof v === 'object' && typeof v.$ta === 'string' && typeof v.d === 'string' && TA[v.$ta]) { const u8 = unb64(v.d); return new TA[v.$ta](u8.buffer, 0, u8.byteLength / TA[v.$ta].BYTES_PER_ELEMENT); }
  return v;
};

/* ---- commands <-> compact tuples [code, dTick, …] ---- */
const fl = c => (c.attack ? 1 : 0) | (c.sprint ? 2 : 0) | (c.auto ? 4 : 0) | (c.jump ? 8 : 0) | (c.special ? 16 : 0);
function packCmd(c, dk) {
  const t = c.t || c.cmd;
  if (t === 'input') return ['i', dk, c.mx, c.mz, c.ax, c.az, fl(c)];
  if (t === 'possess') return ['p', dk, c.i];
  if (t === 'bomb') return ['b', dk, c.x, c.z, c.dmg, c.aoe, c.knock, c.kind];
  if (t === 'throw') return ['w', dk, c.k, c.sx, c.sy, c.sz, c.x, c.z, c.d, c.dmg, c.aoe, c.knock, c.kind]; // feature:thrown
  if (t === 'upg') return ['u', dk, c.id];
  if (t === 'speed') return ['s', dk, c.v];
  return ['o', dk, c];
}
function unpackCmd(a) {
  switch (a[0]) {
    case 'i': return { cmd: 'input', mx: a[2], mz: a[3], ax: a[4], az: a[5], attack: !!(a[6] & 1), sprint: !!(a[6] & 2), auto: !!(a[6] & 4), jump: !!(a[6] & 8), special: !!(a[6] & 16) };
    case 'p': return { cmd: 'possess', i: a[2] };
    case 'b': return { cmd: 'bomb', x: a[2], z: a[3], dmg: a[4], aoe: a[5], knock: a[6], kind: a[7] };
    case 'w': return { cmd: 'throw', k: a[2], sx: a[3], sy: a[4], sz: a[5], x: a[6], z: a[7], d: a[8], dmg: a[9], aoe: a[10], knock: a[11], kind: a[12] }; // feature:thrown
    case 'u': return { t: 'upg', id: a[2] };
    case 's': return { cmd: 'speed', v: a[2] };
    default: return a[2];
  }
}

/** replay object -> JSON text (compact) */
export function toJSON(rep) {
  let k0 = 0, h0 = 0;
  const cmds = rep.cmds.map(([k, c]) => { const t = packCmd(c, k - k0); k0 = k; return t; });
  const hashes = (rep.hashes || []).map(([k, h]) => { const t = [k - h0, h]; h0 = k; return t; });
  const { cmds: _c, hashes: _h, ...head } = rep;
  return JSON.stringify({ ...head, cmds, hashes }, replacer);
}
/** JSON text -> replay object (validated) */
export function fromJSON(text) {
  const o = JSON.parse(text, reviver);
  if (!o || !APP_IDS.includes(o.app) || !o.sim || !Array.isArray(o.cmds)) throw new Error('Das ist keine SpawnAnything-Wiederholung.');
  if (!(o.v <= REPLAY_VERSION)) throw new Error('Diese Wiederholung stammt aus einer neueren Spielversion.');
  let k = 0, h = 0;
  o.cmds = o.cmds.map(a => { k += a[1]; return [k, unpackCmd(a)]; });
  o.hashes = (o.hashes || []).map(a => { h += a[0]; return [h, a[1] >>> 0]; });
  o.ticks = Math.max(0, o.ticks | 0);
  return o;
}

/* ---- gzip (CompressionStream: modern browsers and Node >= 18) ---- */
const canGzip = () => typeof CompressionStream === 'function' && typeof DecompressionStream === 'function';
async function pipeBytes(u8, stream) { return new Uint8Array(await new Response(new Blob([u8]).stream().pipeThrough(stream)).arrayBuffer()); }

/** replay object -> file bytes (gzip-compressed JSON when possible) */
export async function encodeReplay(rep) {
  const raw = new TextEncoder().encode(toJSON(rep));
  return canGzip() ? pipeBytes(raw, new CompressionStream('gzip')) : raw;
}
/** file bytes (gzip or plain JSON) -> replay object; throws with a German message on bad input */
export async function decodeReplay(bytes) {
  let u8 = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  if (u8[0] === 0x1f && u8[1] === 0x8b) {
    if (!canGzip()) throw new Error('Dieser Browser kann komprimierte Wiederholungen nicht öffnen.');
    try { u8 = await pipeBytes(u8, new DecompressionStream('gzip')); } catch (e) { throw new Error('Die Datei ist beschädigt.'); }
  }
  let text;
  try { text = new TextDecoder().decode(u8); } catch (e) { throw new Error('Die Datei ist beschädigt.'); }
  try { return fromJSON(text); } catch (e) { throw e instanceof SyntaxError ? new Error('Die Datei ist beschädigt.') : e; }
}

/** human file name: spawnanything_10000-huehner-vs-1-t-rex.awreplay */
export function replayFileName(title, ext = REPLAY_EXT) {
  const slug = String(title || 'schlacht').toLowerCase().replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60) || 'schlacht';
  return `spawnanything_${slug}${ext}`;
}
